import { getOptimizedImageUrl, getResponsiveSrcSet } from '../lib/cloudinary';
import {
  isR2ImageBase,
  baseFromVariantUrl,
  r2SrcSet,
  r2FallbackUrl,
} from '../lib/images';

interface ResponsiveImageProps {
  /** R2 base URL (preferred). Falls back to `url` when absent. */
  base?: string | null;
  /** Legacy Cloudinary URL, or any plain image URL. */
  url?: string | null;
  alt: string;
  sizes?: string;
  className?: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  /** Widths to request from Cloudinary when falling back to it. */
  cloudinaryWidths?: number[];
  aspectRatio?: number;
}

/**
 * Renders an image as a <picture> so each browser gets the best format it
 * actually supports — AVIF (~95% of traffic), then WebP, then JPEG. That's
 * why there's no global "AVIF or WebP" choice to make: both are served, and
 * the browser picks.
 *
 * Accepts either an R2 base or a legacy Cloudinary URL, so it can be dropped
 * in before the image migration has run.
 */
export default function ResponsiveImage({
  base,
  url,
  alt,
  sizes = '100vw',
  className,
  width,
  height,
  loading = 'lazy',
  fetchPriority,
  cloudinaryWidths = [400, 800, 1600],
  aspectRatio,
}: ResponsiveImageProps) {
  // A blog body image is stored as a concrete `.../800.jpg`; recover the base
  // from it so editor-embedded images get AVIF too, with no editor changes.
  const resolvedBase =
    (base && isR2ImageBase(base) && base) || baseFromVariantUrl(url) || null;

  const commonImgProps = {
    alt,
    className,
    width,
    height,
    loading,
    decoding: 'async' as const,
    ...(fetchPriority ? { fetchPriority } : {}),
  };

  if (resolvedBase) {
    return (
      // `display: contents` keeps <picture> out of the layout entirely, so the
      // <img> sizes against the real container. Without it, className's
      // w-full/h-full resolve against the picture element's zero-width box —
      // which also stops lazy images from ever entering the viewport.
      <picture className="contents">
        <source type="image/avif" srcSet={r2SrcSet(resolvedBase, 'avif')} sizes={sizes} />
        <source type="image/webp" srcSet={r2SrcSet(resolvedBase, 'webp')} sizes={sizes} />
        <img src={r2FallbackUrl(resolvedBase)} {...commonImgProps} />
      </picture>
    );
  }

  if (!url) return null;

  // Cloudinary path — it negotiates format itself via f_auto, so a plain
  // <img> is all that's needed here.
  return (
    <img
      src={getOptimizedImageUrl(url, width ?? 800)}
      srcSet={getResponsiveSrcSet(url, cloudinaryWidths, aspectRatio)}
      sizes={sizes}
      {...commonImgProps}
    />
  );
}
